import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';

import Grid from '../Geodata/grid';
import { Frame } from '../Simulations/Frame.js';
import { Export3DRhino } from './Export3DRhino';
import { Export3DObj } from './Export3DObj';
import { Export3DStl } from './Export3DStl';
import { Export3DGLTF } from './Export3DGLTF';
import { VisualizationElements } from '../Graphics/VisualizationElements.js';

// Store Zustand
import { getColorScheme, getElement, getGlobalState } from '../Gui/store/persistent/persistentStore.js';
import { getNewColorScheme } from '../Gui/store/persistent/persistentStore.js';
import { SerializationManager } from 'Loader/serialization/manager/SerializationManager';
import { resolveSensorColorMode } from '../Gui/store/layerState.js';

import { TreesBatched } from '@data/geodata/tree/TreesBatched';
import { TreesSingleMesh } from '@data/geodata/tree/TreesSingleMesh';
import { type GeoObjectMode } from '@visualizer/types';

import { getGrid } from 'store/LoadingStore';

import { ExportOptions } from './ExportManager';
import { Export3DDXF } from './Export3DDXF';
import type { CoordinateSystemSelection } from '@visualizer/types';
import Layer from 'Simulations/Layer';
import { SensorData } from 'Simulations/SensorPoint';

const DEFAULT_MODE: GeoObjectMode = 'standard';

export class SceneData {
    layerMeshes: THREE.Mesh[][];
    layerNames: string[][];
    layerGroupNames: string[];
    metaData: { [key: string]: string[] };
    geoDataName: string;

    constructor() {
        this.layerMeshes = [];
        this.layerNames = [];
        this.layerGroupNames = [];
        this.metaData = {};
        this.geoDataName = '';
    }
}

interface ExporterOptions {
    exportIndividualBuildings: boolean;
    exportIndividualTrees: boolean;
}

function getActiveGeodata() {
    return getGrid().getGeodata(); // or getGrid().activeGeodata, depending on your store
}

function getActiveSimLayer() {
    return getGrid().getActiveLayer(); // depending on store API
}

export class SceneExporter {
    options: ExporterOptions;
    constructor() {
        this.options = {
            exportIndividualBuildings: false,
            exportIndividualTrees: false,
        };
    }

    // Cancellation
    private cancelRequested: boolean = false;

    public requestCancel() {
        this.cancelRequested = true;
    }

    private throwIfCancelled() {
        if (this.cancelRequested) {
            throw new Error('Export cancelled by user');
        }
    }

    async getSceneData(variant: string, selectedSPs?: Set<string>): Promise<SceneData> {
        const serializationManager = SerializationManager.geomSerializationManager;
        let sceneData: SceneData = new SceneData();

        // get all models (ground, environ. etc.)
        const ground = getActiveGeodata().dataObjectLegacy.Ground;
        const buildings = getActiveGeodata().dataObject.Buildings;
        const trees = getActiveGeodata().dataObject.Trees;
        const roads = getActiveGeodata().dataObjectLegacy.Roads;
        const targets = getActiveGeodata().dataObjectLegacy.Targets;

        // variants : current view | entire scene | custom selection
        var exportGround = true;
        var exportBuilding = true;
        var exportTrees = true;
        var exportRoads = true;
        var exportTargets = true;
        var exportAllSensors = true;
        var exportSelectedSensors = false;

        switch (variant) {
            case 'currentView':
                exportGround = ground ? ground.isVisible() : false;
                exportBuilding = !!buildings;
                exportTrees = trees ? trees.isVisible() : false;
                exportRoads = roads ? roads.isVisible() : false;
                exportTargets = targets ? targets.isVisible() : false;
                exportAllSensors = false;
                break;
            case 'everything':
                break;
            case 'custom':
                exportGround = false;
                exportBuilding = false;
                exportTrees = false;
                exportRoads = false;
                exportTargets = false;
                exportAllSensors = false;
                exportSelectedSensors = true;
                break;
        }

        sceneData.geoDataName = getActiveGeodata().name;

        // get all selected sensorData
        // convert sensorData into a 3D model with or without meta-data
        const framesToExport: Array<{ layer: Layer; frame: Frame; layerName: string }> = [];

        if (exportAllSensors) {
            for (let geodata of getGrid().geodatas) {
                for (let simulationSet of geodata.simulationSets) {
                    for (let layer of simulationSet.layerCollection) {
                        let frames = layer.getFrameCollection();
                        for (let frame of frames) {
                            framesToExport.push({ layer, frame, layerName: layer.name });
                        }
                    }
                }
            }
        } else if (exportSelectedSensors) {
            let selection = Array.from(selectedSPs || []);
            for (let sel of selection) {
                //layer_0_0_0_12;
                const splits = sel.split('-');
                const geodatasIndex = splits.length > 1 ? Number(splits[1]) : -1;
                const simulationSetIndex = splits.length > 2 ? Number(splits[2]) : -1;
                const layerIndex = splits.length > 3 ? Number(splits[3]) : -1;
                let frameIndex = splits.length > 5 ? Number(splits[5]) : -1;
                console.log('Selecting custom SensorPoints ' + sel);
                console.log(geodatasIndex);
                console.log(simulationSetIndex);
                console.log(layerIndex);
                console.log(frameIndex);
                if (layerIndex == -1) continue; // this means that all previous indices are != -1
                if (frameIndex == -1) frameIndex = 0; // if the layer only had one frame, we didnt save the frameIndex
                const layer = getGrid().geodatas[geodatasIndex].simulationSets[simulationSetIndex].layerCollection[layerIndex];
                const frame = layer.getFrameCollection()[frameIndex];
                framesToExport.push({ layer, frame, layerName: layer.name });
            }
        } else {
            const layer = getActiveSimLayer();
            const frame = layer?.getActiveFrame?.();
            if (layer && frame) {
                framesToExport.push({ layer, frame, layerName: layer.name });
            }
        }

        console.log('[SensorExport] multimode=', getGlobalState().data.multimode);
        console.log('[SensorExport] framesToExport=', framesToExport.length);
        console.log('[SensorExport] activeLayer=', getActiveSimLayer()?.name);
        console.log('[SensorExport] activeFrame=', getActiveSimLayer()?.getActiveFrame()?.getName?.());

        for (let i = 0; i < framesToExport.length; i++) {
            const entry = framesToExport[i];
            if (!entry) continue;

            const { layer, frame, layerName } = entry;

            const sensors = frame.getSensors();
            const sensorType = getElement().SEL_VIS_ELEMENT;

            const meshLayers: any[] = [];
            const layerNames: string[] = [];

            let total = 0,
                skippedScalar = 0,
                skippedInactive = 0,
                exported = 0;

            // Run pipeline
            const pipelineResult = layer.runPipeline?.({ suppressNotifications: true, octTree: getGrid().octTree! });
            const pipelineSensors = pipelineResult?.data ?? [];

            // Build set of active sensor IDs from pipeline output
            const activeIds = new Set<string>(pipelineSensors.map((s: any) => String(s.getID?.() ?? s.id)));

            const filterByPipeline = !exportAllSensors;

            // Compute min/max f
            // rom pipeline output
            let minV = Infinity;
            let maxV = -Infinity;

            for (const s of pipelineSensors) {
                const v = s.getScalarValue?.() ?? s.getScalarValue?.();
                if (v == null) continue;
                minV = Math.min(minV, v);
                maxV = Math.max(maxV, v);
            }

            if (!isFinite(minV) || !isFinite(maxV) || minV === maxV) {
                minV = 0;
                maxV = 1;
            }

            // Export sensors
            for (const sensor of sensors) {
                total++;

                if (sensor.getScalarValue?.() === 0) skippedScalar++;

                const id = String(sensor.getID?.() ?? sensor.getID());

                // Replace fake sensor.isActive() with pipeline truth
                if (filterByPipeline && !activeIds.has(id)) {
                    skippedInactive++;
                    continue;
                }

                const mesh = this.getSensorMesh(sensor, sensorType, minV, maxV);

                const name = mesh.name;
                meshLayers.push(mesh);
                layerNames.push(name);

                const metaDataName = layerName + '_' + name;
                if (!sceneData.metaData[metaDataName]) {
                    sceneData.metaData[metaDataName] = [];
                }
                sceneData.metaData[metaDataName] = SceneExporter.getSensorMetaData(sensor, frame);

                exported++;
            }

            console.log(
                `[SensorExport] total=${total} skippedScalar=${skippedScalar} skippedInactive=${skippedInactive} exported=${exported}`
            );

            if (meshLayers.length > 0) {
                sceneData.layerGroupNames.push('SensorData:' + layerName + frame.getName());
                sceneData.layerMeshes.push(meshLayers as any);
                sceneData.layerNames.push(layerNames);
            }
        }

        // TODO: Implement this with the new DesignSchemeSetManager.
        // const designSchemes = designSchemeManager.getAllDesignSchemes();
        // if (designSchemes.length > 0) {
        //     let meshLayers = [];
        //     let layerNames = [];
        //     for (let scheme of designSchemes) {
        //         let name = typeof scheme;
        //         for (let mesh of scheme.meshGroup.children) {
        //             meshLayers.push(mesh.clone() as THREE.Mesh);
        //             layerNames.push(name);
        //         }
        //     }
        //     sceneData.layerGroupNames.push('DesignSchemes');
        //     sceneData.layerMeshes.push(meshLayers);
        //     sceneData.layerNames.push(layerNames);
        // }

        if (ground && exportGround) {
            const names = ['Ground:Near', 'Ground:Mid', 'Ground:Far', 'Ground:Other'];
            let meshLayers = [];
            let layerNames = [];
            for (let i = 0; i < ground.trianglesMesh.children.length; i++) {
                let clonedMesh = ground.trianglesMesh.children[i].clone();
                meshLayers.push(clonedMesh as THREE.Mesh);
                layerNames.push(names[Math.min(i, names.length - 1)]);
            }
            sceneData.layerGroupNames.push('Ground');
            sceneData.layerMeshes.push(meshLayers);
            sceneData.layerNames.push(layerNames);
        }

        if (buildings && exportBuilding) {
            const exported = buildings.export();
            if (exported.length > 0) {
                sceneData.layerGroupNames.push('Buildings');
                sceneData.layerMeshes.push(exported.map((b) => b.mesh));
                sceneData.layerNames.push(exported.map((b) => b.name));
            }
        }

        if (trees && exportTrees) {
            const data = await serializationManager.deserializeGeom(getActiveGeodata().filesMap.get('Vegetation')!, 'Trees', false);
            const cs = getNewColorScheme();

            const clazz = this.options.exportIndividualTrees ? TreesBatched : TreesSingleMesh;
            const t = new clazz({
                name: 'Trees',
                colors: cs,
                data,
                mode: DEFAULT_MODE,
            });

            t.setMode(DEFAULT_MODE);

            const exported = t.export();
            sceneData.layerGroupNames.push('Trees');
            sceneData.layerMeshes.push(exported.map((b) => b.mesh));
            sceneData.layerNames.push(exported.map((b) => b.name));

            // exports clones the meshes to we can dispose of this
            t.dispose();
        }

        if (roads && exportRoads) {
            const names = ['Roads', 'Roads:Other'];
            let meshLayers = [];
            let layerNames = [];
            // Export roads with noise level colors preserved
            for (let i = 0; i < roads.strokesMesh.children.length; i++) {
                const roadMesh = roads.strokesMesh.children[i].clone() as LineSegments2;
                // Preserve the color information from the original road mesh
                // The colors are already set based on emission/noise levels in Roads.ts
                meshLayers.push(roadMesh);
                layerNames.push(names[0]);
            }
            // Also export individual road segments with noise level data if available
            if (roads.roads && roads.roads.length > 0) {
                // Create metadata for noise levels
                const noiseLevels = roads.roads.map((road) => road.emission.toString());
                sceneData.metaData['RoadNoiseLevels'] = noiseLevels;

                // Add min/max emission values for reference
                sceneData.metaData['MinEmissionValue'] = [roads.minEmissionValue.toString()];
                sceneData.metaData['MaxEmissionValue'] = [roads.maxEmissionValue.toString()];
            }
            sceneData.layerGroupNames.push('Roads');
            sceneData.layerMeshes.push(meshLayers);
            sceneData.layerNames.push(layerNames);
        }

        if (targets && exportTargets) {
            const meshLayers: LineSegments2[] = [];
            const layerNames: string[] = [];
            if (meshLayers.length > 0) {
                sceneData.layerGroupNames.push('Targets');
                sceneData.layerMeshes.push(meshLayers);
                sceneData.layerNames.push(layerNames);
            }
        }

        return sceneData;
    }

    async getFormattedData(
        sceneData: SceneData,
        format: string,
        filename: string,
        setProgress: (progress: number | undefined) => void,
        options?: ExportOptions
    ): Promise<{ formattedData: Uint8Array; extension?: string }> {
        // Check for cancellation
        this.throwIfCancelled();

        // write all models to file
        switch (format) {
            case '3DM':
                return await Export3DRhino(sceneData, setProgress, () => this.cancelRequested, options?.coordinateSystem);
            case 'OBJ':
                // we're sending the filename to the exporter
                // as OBJ have to save a material .MTL file
                // alongside the actual scene data
                return await Export3DObj(sceneData, filename, setProgress);
            case 'STL':
                return await Export3DStl(sceneData, setProgress, options?.orientation3d);
            case 'DXF':
                return await Export3DDXF(sceneData, setProgress, options?.orientation3d);
            case 'GLTF':
                return await Export3DGLTF(sceneData, setProgress);
        }
        return { formattedData: new Uint8Array() };
    }

    getSensorMesh(sensor: SensorData, sensorType: number, minV: number, maxV: number): THREE.Mesh {
        const center = sensor.getPosition();
        // Todo: ask what to do here for the getScale and getcolor methods not present in SensorPoint, for now tried to fix as possible
        const scale = Grid.gridRes * 0.5;

        const geometry = VisualizationElements.getGeometry(sensorType as any);

        const v = sensor.getScalarValue?.() ?? 0;
        const t = (v - minV) / (maxV - minV);
        const clamped = Math.min(1, Math.max(0, t));

        const sensorColorMode = resolveSensorColorMode(getElement());
        let col: THREE.Color;
        if (sensorColorMode === 2) {
            const minLum = 0.2;
            const maxLum = 0.85;
            const lum = (minLum + maxLum) / 2;
            col = new THREE.Color().setHSL(0, 0, Math.min(1, Math.max(0, lum)));
        } else if (sensorColorMode === 1) {
            const minLum = 0.2;
            const maxLum = 0.85;
            const lum = maxLum + (minLum - maxLum) * clamped;
            col = new THREE.Color().setHSL(0, 0, Math.min(1, Math.max(0, lum)));
        } else {
            // Use your scheme (you already use it for buildings/trees)
            // Use your scheme (you already use it for buildings/trees)
            const cs = getColorScheme();

            // NOTE: your cs.GRID_FILL_H is [0..100] percent in your codebase patterns
            const minHue = cs.GRID_FILL_H[0] / 100;
            const maxHue = cs.GRID_FILL_H[1] / 100;

            // Same interpolation style as your renderer usually does
            const hue = maxHue + (minHue - maxHue) * clamped;

            col = new THREE.Color().setHSL(hue, cs.GRID_FILL_S, cs.GRID_FILL_L);
        }
        const material = new THREE.MeshBasicMaterial({ color: col });

        const direction = sensor.getVectorialResult();
        const position = new THREE.Vector3(center.x, center.z, -center.y);
        const exportDirection = new THREE.Vector3(direction.x, direction.z, -direction.y);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = 'Sensor' + sensor.getID();
        mesh.scale.set(scale, scale, scale);
        mesh.position.set(position.x, position.y, position.z);

        if (sensorType != 0) {
            // Match LayerDraw's shader orientation: rotate local +Z onto the sensor direction.
            const lengthSq = exportDirection.lengthSq();
            if (lengthSq > 1e-10) {
                mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), exportDirection.normalize());
            }
        }
        mesh.updateMatrix();

        return mesh;
    }

    static getSensorMetaData(sensor: SensorData, _frame?: Frame): string[] {
        const center = sensor.getPosition();

        const sensorMetaData: string[] = [];
        sensorMetaData.push('Scalar value' + '|' + sensor.getScalarValue());
        sensorMetaData.push('Frame Max' + '|' + 'undefined');
        sensorMetaData.push('Frame Min' + '|' + 'undefined');
        sensorMetaData.push('Pos' + '|' + 'x: ' + center.x + ' y: ' + center.y + ' z: ' + center.z);

        return sensorMetaData;
    }

    applyCoordinateSystemTransform(
        sceneData: SceneData,
        coordinateSystem?: CoordinateSystemSelection,
        orientation3d: 'Y_Up' | 'Z_Up' = 'Y_Up'
    ) {
        if (!coordinateSystem || coordinateSystem.mode !== 'custom') return;

        const { centerX, centerY, centerZ, alpha } = coordinateSystem;
        const angle = THREE.MathUtils.degToRad(alpha);
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const nationalOrigin = Grid.gridOrigin;

        const transformPoint = (point: THREE.Vector3) => {
            if (orientation3d === 'Z_Up') {
                const x = point.x;
                const y = point.y;
                const z = point.z;

                const worldX = nationalOrigin.x + x;
                const worldY = nationalOrigin.y + z;
                const worldZ = nationalOrigin.z + y;

                const translatedX = worldX - centerX;
                const translatedY = worldY - centerY;
                const rotatedX = translatedX * cos + translatedY * sin;
                const rotatedY = -translatedX * sin + translatedY * cos;

                point.set(rotatedX, worldZ - centerZ, rotatedY);
                return;
            }

            const x = point.x;
            const y = point.y;
            const z = point.z;

            const worldX = nationalOrigin.x + x;
            const worldY = nationalOrigin.y + y;
            const worldZ = nationalOrigin.z + z;

            const translatedX = worldX - centerX;
            const translatedY = worldY - centerY;
            const rotatedX = translatedX * cos + translatedY * sin;
            const rotatedY = -translatedX * sin + translatedY * cos;

            point.set(rotatedX, rotatedY, worldZ - centerZ);
        };

        const transformAttribute = (array: ArrayLike<number>) => {
            const transformed = new Float32Array(array.length);
            const point = new THREE.Vector3();

            for (let i = 0; i < array.length; i += 3) {
                point.set(array[i], array[i + 1], array[i + 2]);
                transformPoint(point);
                transformed[i] = point.x;
                transformed[i + 1] = point.y;
                transformed[i + 2] = point.z;
            }

            return transformed;
        };

        for (const meshGroup of sceneData.layerMeshes) {
            for (const root of meshGroup) {
                root.traverse((obj) => {
                    const geom = (obj as THREE.Object3D & { geometry?: THREE.BufferGeometry }).geometry;
                    if (geom?.isBufferGeometry) {
                        const position = geom.getAttribute('position');
                        if (position) {
                            const transformed = transformAttribute(position.array);
                            position.array.set(transformed);
                            position.needsUpdate = true;
                            geom.computeBoundingBox();
                            geom.computeBoundingSphere();
                        }
                    }

                    if (obj.type !== 'LineSegments2') return;

                    const line = obj as any;
                    const startAttr = line.geometry.attributes?.instanceStart;
                    const endAttr = line.geometry.attributes?.instanceEnd;
                    const startArray = startAttr?.data?.array ?? startAttr?.array;
                    const endArray = endAttr?.data?.array ?? endAttr?.array;

                    if (startArray) {
                        const transformed = transformAttribute(startArray);
                        if (startAttr?.data?.array) {
                            startAttr.data.array.set(transformed);
                            startAttr.data.needsUpdate = true;
                        } else {
                            startAttr.array.set(transformed);
                            startAttr.needsUpdate = true;
                        }
                    }

                    if (endArray) {
                        const transformed = transformAttribute(endArray);
                        if (endAttr?.data?.array) {
                            endAttr.data.array.set(transformed);
                            endAttr.data.needsUpdate = true;
                        } else {
                            endAttr.array.set(transformed);
                            endAttr.needsUpdate = true;
                        }
                    }
                });
            }
        }
    }

    rotateToStandardCoordinateSpace(sceneData: SceneData) {
        for (const meshGroup of sceneData.layerMeshes) {
            for (const mesh of meshGroup) {
                if ((mesh.name || '').startsWith('Sensor')) continue;

                mesh.rotateX(-Math.PI / 2); // rotate to a more reasonable coordinate space
                mesh.updateMatrixWorld();
                mesh.updateMatrix();
            }
        }
    }

    normalizeSceneDataFor3DExport(sceneData: SceneData, _orientation3d: 'Y_Up' | 'Z_Up' = 'Y_Up'): SceneData {
        return this.prepareSceneDataFor3DExport(sceneData);
    }

    prepareSceneDataFor3DExport(sceneData: SceneData): SceneData {
        const normalized = new SceneData();

        normalized.geoDataName = sceneData.geoDataName;
        normalized.metaData = { ...sceneData.metaData };
        normalized.layerGroupNames = [...sceneData.layerGroupNames];
        normalized.layerNames = sceneData.layerNames.map((names) => [...names]);

        const axisFix = new THREE.Matrix4().identity();

        const transformLineArray = (array: ArrayLike<number>, matrix: THREE.Matrix4) => {
            const out = new Float32Array(array.length);
            const v = new THREE.Vector3();

            for (let i = 0; i < array.length; i += 3) {
                v.set(array[i], array[i + 1], array[i + 2]).applyMatrix4(matrix);
                out[i] = v.x;
                out[i + 1] = v.y;
                out[i + 2] = v.z;
            }

            return out;
        };

        const isZeroScaleMatrix = (matrix: THREE.Matrix4) => {
            const scale = new THREE.Vector3();
            matrix.decompose(new THREE.Vector3(), new THREE.Quaternion(), scale);
            const eps = 1e-6;
            return Math.abs(scale.x) < eps && Math.abs(scale.y) < eps && Math.abs(scale.z) < eps;
        };

        const bakeBatchedMesh = (batchedMesh: THREE.Mesh & any): THREE.Mesh[] => {
            const sourceGeometry = batchedMesh.geometry as THREE.BufferGeometry;
            const sourcePosition = sourceGeometry.getAttribute('position') as THREE.BufferAttribute | undefined;
            if (!sourcePosition) return [];

            const bakedMeshes: THREE.Mesh[] = [];
            const sourceIndex = sourceGeometry.getIndex();
            const instanceMatrix = new THREE.Matrix4();
            const totalMatrix = new THREE.Matrix4();
            const vertex = new THREE.Vector3();
            const instanceCount = Array.isArray(batchedMesh._instanceInfo)
                ? batchedMesh._instanceInfo.length
                : (batchedMesh.instanceCount ?? 0);

            for (let instanceId = 0; instanceId < instanceCount; instanceId++) {
                try {
                    if (typeof batchedMesh.getVisibleAt === 'function' && !batchedMesh.getVisibleAt(instanceId)) continue;

                    const geometryId = batchedMesh.getGeometryIdAt(instanceId);
                    const range = batchedMesh.getGeometryRangeAt(geometryId, {});
                    batchedMesh.getMatrixAt(instanceId, instanceMatrix);
                    if (isZeroScaleMatrix(instanceMatrix)) continue;

                    totalMatrix.multiplyMatrices(batchedMesh.matrixWorld, instanceMatrix);
                    totalMatrix.premultiply(axisFix);

                    const positions: number[] = [];
                    const pushVertex = (vertexIndex: number) => {
                        vertex.fromBufferAttribute(sourcePosition, vertexIndex).applyMatrix4(totalMatrix);
                        positions.push(vertex.x, vertex.y, vertex.z);
                    };

                    if (sourceIndex && range.indexStart >= 0) {
                        for (let i = range.indexStart; i < range.indexStart + range.indexCount; i++) {
                            pushVertex(sourceIndex.getX(i));
                        }
                    } else {
                        for (let i = range.vertexStart; i < range.vertexStart + range.vertexCount; i++) {
                            pushVertex(i);
                        }
                    }

                    if (positions.length === 0) continue;

                    const bakedGeometry = new THREE.BufferGeometry();
                    bakedGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
                    bakedGeometry.computeVertexNormals();
                    bakedGeometry.computeBoundingBox();
                    bakedGeometry.computeBoundingSphere();

                    const bakedMesh = new THREE.Mesh(bakedGeometry, batchedMesh.material);
                    bakedMesh.name = `${batchedMesh.name || 'BatchedMesh'}_${instanceId}`;
                    bakedMesh.userData = { ...batchedMesh.userData };
                    bakedMesh.updateMatrix();
                    bakedMesh.updateMatrixWorld(true);
                    bakedMeshes.push(bakedMesh);
                } catch {
                    // Deleted BatchedMesh instance ids throw from the public accessors.
                }
            }

            return bakedMeshes;
        };

        normalized.layerMeshes = sceneData.layerMeshes.map((roots) =>
            roots.map((root) => {
                const clonedRoot = root.clone(true) as THREE.Object3D;
                clonedRoot.updateMatrixWorld(true);

                const bakedRoot = new THREE.Group();
                bakedRoot.name = clonedRoot.name || 'ExportRoot';

                clonedRoot.traverse((obj) => {
                    if ((obj as any).isBatchedMesh === true) {
                        bakeBatchedMesh(obj as THREE.Mesh & any).forEach((mesh) => bakedRoot.add(mesh));
                        return;
                    }

                    // Handle normal geometry-bearing objects: Mesh, Points, Line, etc.
                    const geomObj = obj as THREE.Object3D & {
                        geometry?: THREE.BufferGeometry;
                        material?: any;
                    };

                    if (geomObj.geometry && geomObj.geometry.isBufferGeometry) {
                        const bakedObj = (obj as any).clone(false);
                        const bakedGeometry = geomObj.geometry.clone();

                        bakedGeometry.applyMatrix4(obj.matrixWorld);
                        bakedGeometry.applyMatrix4(axisFix);

                        bakedObj.geometry = bakedGeometry;
                        bakedObj.position.set(0, 0, 0);
                        bakedObj.rotation.set(0, 0, 0);
                        bakedObj.scale.set(1, 1, 1);
                        bakedObj.updateMatrix();
                        bakedObj.updateMatrixWorld(true);

                        bakedRoot.add(bakedObj);
                        return;
                    }

                    // Handle LineSegments2 separately
                    if (obj.type === 'LineSegments2') {
                        const line = obj as any;
                        const bakedLine = line.clone(false);
                        const geom = line.geometry.clone();

                        const totalMatrix = new THREE.Matrix4().multiplyMatrices(axisFix, obj.matrixWorld);

                        const startAttr = geom.attributes?.instanceStart;
                        const endAttr = geom.attributes?.instanceEnd;

                        const startArray = startAttr?.data?.array ?? startAttr?.array;
                        const endArray = endAttr?.data?.array ?? endAttr?.array;

                        if (startArray) {
                            const bakedStart = transformLineArray(startArray, totalMatrix);
                            if (startAttr?.data?.array) {
                                startAttr.data.array.set(bakedStart);
                                startAttr.data.needsUpdate = true;
                            } else if (startAttr?.array) {
                                startAttr.array.set(bakedStart);
                                startAttr.needsUpdate = true;
                            }
                        }

                        if (endArray) {
                            const bakedEnd = transformLineArray(endArray, totalMatrix);
                            if (endAttr?.data?.array) {
                                endAttr.data.array.set(bakedEnd);
                                endAttr.data.needsUpdate = true;
                            } else if (endAttr?.array) {
                                endAttr.array.set(bakedEnd);
                                endAttr.needsUpdate = true;
                            }
                        }

                        bakedLine.geometry = geom;
                        bakedLine.position.set(0, 0, 0);
                        bakedLine.rotation.set(0, 0, 0);
                        bakedLine.scale.set(1, 1, 1);
                        bakedLine.updateMatrix();
                        bakedLine.updateMatrixWorld(true);

                        bakedRoot.add(bakedLine);
                    }
                });

                bakedRoot.updateMatrixWorld(true);
                return bakedRoot as any;
            })
        );

        return normalized;
    }
}
